import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import {
  HelpCircle,
  Menu,
  User,
  Settings,
  Sparkles,
  Shield,
  LogOut,
  LayoutGrid,
  Columns,
  Eye,
  Zap,
  LifeBuoy,
  FolderOpen,
  Package,
} from 'lucide-react';
import SiteLogo from '@/components/SiteLogo';
import UserMenu from '@/components/UserMenu';
import { useAuth } from '@/contexts/AuthContext';
import { useEntitlements } from '@/contexts/entitlement-context';

interface SiteHeaderProps {
  /** Optional click handler for the logo. Defaults to navigating to "/". */
  onLogoClick?: () => void;
}

const SiteHeader: React.FC<SiteHeaderProps> = ({ onLogoClick }) => {
  const navigate = useNavigate();
  const { user, authReady, isAdmin, signOut } = useAuth();
  const { currentPlan, loading: entitlementsLoading } = useEntitlements();
  const isSignedIn = authReady && !!user;
  const tierName = currentPlan?.name ?? null;
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogo = () => {
    if (onLogoClick) onLogoClick();
    else navigate('/');
  };

  const go = (path: string) => {
    setMobileOpen(false);
    navigate(path);
  };

  const handleSignOut = async () => {
    setMobileOpen(false);
    await signOut();
    localStorage.removeItem('storage_consent');
    navigate('/auth', { replace: true });
  };

  return (
    <header className="relative z-30 bg-black/20 backdrop-blur-md border-b border-white/20">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
        <SiteLogo
          size="lg"
          className="scale-90 sm:scale-100 cursor-pointer"
          onClick={handleLogo}
        />

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-2">
          {/* Accessories moved into Configure tabs */}

          {isSignedIn && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/projects')}
              className="text-white hover:bg-white/10"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Projects
            </Button>
          )}
          {isSignedIn && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/kit')}
              className="text-white hover:bg-white/10"
            >
              <Package className="w-4 h-4 mr-2" />
              My Kit
            </Button>
          )}


          {isSignedIn && !entitlementsLoading && tierName && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/pricing')}
              title="Your plan — click to manage"
              aria-label={`Current plan: ${tierName}. Click to manage.`}
              className="text-white/90 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10"
            >
              {tierName}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/help')}
            aria-label="Open help"
            className="text-white hover:bg-white/10"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Help
          </Button>
          {!authReady ? (
            <div aria-hidden className="h-9 w-[160px]" />
          ) : isSignedIn ? (
            <UserMenu />
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/auth')}
                className="text-white bg-transparent border border-white/25 hover:bg-white/10 hover:text-white"
              >
                Sign in
              </Button>
              <Button
                size="sm"
                onClick={() => navigate('/auth?tab=signup')}
                className="bg-gradient-to-r from-brand-blue to-brand-magenta text-white hover:opacity-90 font-semibold"
              >
                Sign up
              </Button>
            </div>
          )}
        </div>

        {/* Mobile hamburger */}
        <div className="flex md:hidden items-center">
          {!authReady ? (
            <div aria-hidden className="h-9 w-9" />
          ) : (
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open navigation menu"
                  className="text-white hover:bg-white/10"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="bg-slate-950/95 backdrop-blur-xl border-white/10 text-white w-72 sm:max-w-xs"
              >
                <SheetHeader>
                  <SheetTitle className="text-white text-left">Menu</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 flex flex-col gap-1">
                  {isSignedIn && (
                    <Button
                      variant="ghost"
                      onClick={() => go('/projects')}
                      className="w-full justify-start text-white hover:bg-white/10"
                    >
                      <FolderOpen className="w-4 h-4 mr-3" />
                      Projects
                    </Button>
                  )}
                  {isSignedIn && (
                    <Button
                      variant="ghost"
                      onClick={() => go('/kit')}
                      className="w-full justify-start text-white hover:bg-white/10"
                    >
                      <Package className="w-4 h-4 mr-3" />
                      My Kit
                    </Button>
                  )}
                  {isSignedIn && (
                    <Button
                      variant="ghost"
                      onClick={() => go('/brands')}
                      className="w-full justify-start text-white hover:bg-white/10"
                    >
                      <Zap className="w-4 h-4 mr-3" />
                      Configure
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => go('/compare')}
                    className="w-full justify-start text-white hover:bg-white/10"
                  >
                    <Columns className="w-4 h-4 mr-3" />
                    Compare
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => go('/pitch-compare')}
                    className="w-full justify-start text-white hover:bg-white/10"
                  >
                    <Eye className="w-4 h-4 mr-3" />
                    Pitch Compare
                  </Button>

                  <div className="my-2 h-px bg-white/10" />

                  <Button
                    variant="ghost"
                    onClick={() => go('/help')}
                    className="w-full justify-start text-white hover:bg-white/10"
                  >
                    <HelpCircle className="w-4 h-4 mr-3" />
                    Help
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => go('/support')}
                    className="w-full justify-start text-white hover:bg-white/10"
                  >
                    <LifeBuoy className="w-4 h-4 mr-3" />
                    Support
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => go('/pricing')}
                    className="w-full justify-start text-white hover:bg-white/10"
                  >
                    <Sparkles className="w-4 h-4 mr-3" />
                    Pricing & Plans
                  </Button>

                  {isSignedIn ? (
                    <>
                      <div className="my-2 h-px bg-white/10" />
                      {tierName && (
                        <button
                          type="button"
                          onClick={() => go('/billing')}
                          aria-label={`Current plan: ${tierName}. Manage subscription.`}
                          className="w-full flex items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm text-white/90 hover:bg-white/10"
                        >
                          <span className="flex items-center gap-3 min-w-0">
                            <Sparkles className="w-4 h-4 shrink-0 text-brand-magenta" />
                            <span className="truncate">
                              <span className="text-white/50">Plan:</span> {tierName}
                            </span>
                          </span>
                          <span className="text-xs text-white/60 shrink-0">Manage →</span>
                        </button>
                      )}
                      <Button
                        variant="ghost"
                        onClick={() => go('/account')}
                        className="w-full justify-start text-white hover:bg-white/10"
                      >
                        <Settings className="w-4 h-4 mr-3" />
                        My Account
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          onClick={() => go('/admin')}
                          className="w-full justify-start text-white hover:bg-white/10"
                        >
                          <Shield className="w-4 h-4 mr-3" />
                          Admin Panel
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        onClick={handleSignOut}
                        className="w-full justify-start text-white hover:bg-white/10"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="my-2 h-px bg-white/10" />
                      <SheetClose asChild>
                        <Button
                          onClick={() => navigate('/auth?tab=signup')}
                          className="w-full bg-gradient-to-r from-brand-blue to-brand-magenta text-white hover:opacity-90 font-semibold"
                        >
                          Create free account
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button
                          onClick={() => navigate('/auth')}
                          variant="ghost"
                          className="w-full justify-center text-white bg-transparent border border-white/25 hover:bg-white/10"
                        >
                          Sign in
                        </Button>
                      </SheetClose>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  );
};

export default SiteHeader;
